import type { Dispatch } from 'react';

import { RevelioPageId } from '../../../next/CustomDash/components/PageConfigProvider/types';
import type { FilterAction } from '../../../next/CustomDash/filters/engine/filterReducer';
import { FilterId } from '../../../next/CustomDash/filters/types/filters';
import {
  PreferenceScope,
  type UserPreferenceKey,
  type UserPreferenceSubKey,
  type UserPreferences,
} from '../types';
import { buildFilterUpdatesFromPreferences } from './preference-filter-adapters';

const NO_PRIMARY_FILTER_KEY = 'NO_PRIMARY_FILTER' as const;

export const getPreferenceScope = ({
  pageId,
  primaryFilterId,
}: {
  pageId?: RevelioPageId | string;
  primaryFilterId: string;
}): PreferenceScope => {
  // Page specific scopes
  if (pageId === RevelioPageId.TALENT_DISCOVERY) {
    return PreferenceScope.INDIVIDUAL;
  }
  // Screener v2 pages have no primary filter
  // scope to their respective tab (company, role, geo)
  if (pageId === RevelioPageId.COMPANY_SCREENER_V2) {
    return PreferenceScope.COMPANY;
  }
  if (pageId === RevelioPageId.GEOGRAPHY_SCREENER_V2) {
    return PreferenceScope.GEOGRAPHY;
  }
  if (pageId === RevelioPageId.ROLE_SCREENER_V2) {
    return PreferenceScope.ROLE;
  }
  // Scopes based on primary filter
  if (primaryFilterId === NO_PRIMARY_FILTER_KEY) {
    return PreferenceScope.GLOBAL;
  } else if (primaryFilterId === FilterId.COMPANY) {
    return PreferenceScope.COMPANY;
  } else if (primaryFilterId === FilterId.GEOGRAPHY) {
    return PreferenceScope.GEOGRAPHY;
  } else if (primaryFilterId === FilterId.ROLE) {
    return PreferenceScope.ROLE;
  } else {
    return PreferenceScope.GLOBAL;
  }
};

// Apply UI preferences to the filters state via dispatch.
//
// `source: 'preference-sync'` marks AUTOMATED syncs (the
// `SyncInitialPreferencesToFilters` effect): the reducer skips such a write
// when it targets a filter whose live state is `isAiApplied`, so a sync can
// never unpair an AI-applied taxonomy from its taxonomy-relative ids. Direct
// user actions (the Preferences modal via
// `updateCustomDashFiltersForPreference`) stay unmarked and override normally.
export function updateCustomDashFilters(
  dispatch: Dispatch<FilterAction>,
  preferences: Partial<UserPreferences>,
  subKey?: UserPreferenceSubKey,
  source?: 'preference-sync'
): void {
  const updates = buildFilterUpdatesFromPreferences(preferences, subKey);
  // biome-ignore lint/suspicious/useIterableCallbackReturn: migrated from develop
  updates.forEach((update) =>
    dispatch({
      type: 'UPDATE_FILTER',
      filter: update,
      ...(source ? { source } : {}),
    })
  );
}

export function updateCustomDashFiltersForPreference(
  dispatch: Dispatch<FilterAction>,
  preference: UserPreferenceKey,
  value: UserPreferences[UserPreferenceKey],
  subKey?: UserPreferenceSubKey
): void {
  updateCustomDashFilters(dispatch, { [preference]: value }, subKey);
}
